import React, { useId } from 'react';

/**
 * The house switch, and the row it usually sits in.
 *
 * Variant C's `.sw` is a styled `<span>` that its demo script toggles: no
 * keyboard, no name, nothing for assistive tech. Here it wraps a real
 * `<input type="checkbox">` and drives the same visuals off `:checked`, so it
 * looks identical and behaves like a control. See the note beside `.sw-input`
 * in styles/variant-c/overrides.css.
 *
 * `CheckRow` is the demo's `.check-row`: the question on the left, the switch
 * pushed to the right edge by `.check-row .sw { margin-left: auto }`. The whole
 * row is the label, so the text is the hit area too.
 *
 * `id` comes from `useId` rather than from `name`. The input is inside its own
 * label, so nothing here needs the id to point at it — but an id has to be
 * unique in the document, and `name` is not: two settings rows given the same
 * `name` on one page would have produced two elements sharing one id, which is
 * invalid and sends `document.getElementById` and assistive tech to whichever
 * came first.
 */
export const Switch = ({ name, checked, onChange, label, disabled = false }) => {
    const id = useId();
    return (
        <label className="sw-label">
            <input
                type="checkbox"
                className="sw-input"
                name={name}
                id={id}
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                aria-label={label}
            />
            <span className="sw" />
        </label>
    );
};

export const CheckRow = ({ name, checked, onChange, children, className = '', icon }) => {
    const id = useId();
    return (
        <label className={`check-row ${className}`.trim()}>
            {icon}
            {children}
            <input
                type="checkbox"
                className="sw-input"
                name={name}
                id={id}
                checked={checked}
                onChange={onChange}
            />
            <span className="sw" />
        </label>
    );
};

export default Switch;
